import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';

// Import all theme creators
import { createVibrantTheme } from '../themes/VibrantTheme';
import { createMinimalistTheme } from '../themes/MinimalistTheme';
import { createCorporateTheme } from '../themes/CorporateTheme';
import { createMaterialYouTheme } from '../themes/MaterialYouTheme';
import { createSunsetTheme } from '../themes/SunsetTheme';
import { createOceanTheme } from '../themes/OceanTheme';

// Import ThemeSelector
import ThemeSelector from './common/ThemeSelector';

const AppThemeProvider = ({ children }) => {
  // Get saved preferences from localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  const [themeVariant, setThemeVariant] = useState(() => {
    const savedTheme = localStorage.getItem('themeVariant');
    return 'vibrant'; // Default to vibrant theme
  });
  
  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('darkMode', newMode.toString());
      return newMode;
    });
  };
  
  // Change theme variant
  const handleThemeChange = (newTheme) => {
    setThemeVariant(newTheme);
    localStorage.setItem('themeVariant', newTheme);
  };

  const showThemeSelector = false; // Set to false to hide the theme selector button
  
  // Create the appropriate theme
  const theme = useMemo(() => {
    switch (themeVariant) {
      case 'vibrant':
        return createVibrantTheme(darkMode ? 'dark' : 'light');
      case 'minimalist':
        return createMinimalistTheme(darkMode ? 'dark' : 'light');
      case 'corporate':
        return createCorporateTheme(darkMode ? 'dark' : 'light');
      case 'materialYou':
        return createMaterialYouTheme(darkMode ? 'dark' : 'light');
      case 'sunset':
        return createSunsetTheme(darkMode ? 'dark' : 'light');
      case 'ocean':
        return createOceanTheme(darkMode ? 'dark' : 'light');
      default:
        return createVibrantTheme(darkMode ? 'dark' : 'light');
    }
  }, [themeVariant, darkMode]);
  
  
  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (localStorage.getItem('darkMode') === null) {
        setDarkMode(e.matches);
      }
    };
    
    // Use the appropriate event listener based on browser support
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }
    
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);
  
  // Update the useEffect to actually apply the class to the body for CSS selectors
  useEffect(() => {
    // Add or remove dark mode class on body
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Add theme variant class to body
    document.body.className = document.body.className
      .split(' ')
      .filter(c => !c.startsWith('theme-'))
      .join(' ');
    document.body.classList.add(`theme-${themeVariant}`);
    
  }, [darkMode, themeVariant]);
  
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {React.cloneElement(children, { 
        darkMode, 
        toggleDarkMode,
        themeVariant,
      })}
      {(showThemeSelector && 
      <ThemeSelector 
        currentTheme={themeVariant} 
        onThemeChange={handleThemeChange} 
      />)}
    </MuiThemeProvider>
  );
};

export default AppThemeProvider;